import { ChangeEvent, useCallback, useEffect, useRef } from 'react';
import { Box, BoxProps, Icon, Popover, PopoverContent, PopoverTrigger } from '@bitrise/bitkit';
import { useOnClickOutside } from 'usehooks-ts';

import useSearch from '../useSearch';
import AppList from './AppList';

export type SearchBarProps = {
  inputTabIndex: number;
  onClose: () => void;
} & BoxProps;

const SearchBar = ({ inputTabIndex, onClose, ...props }: SearchBarProps): JSX.Element => {
  const { query, setQuery, appList, isLoading } = useSearch();

  const inputRef = useRef<HTMLInputElement>(null);
  const handleClose = useCallback(() => {
    onClose();
    setQuery('');
    inputRef.current?.blur();
  }, [onClose, setQuery]);

  useEffect(() => {
    const eventListener = ({ code }: KeyboardEvent) => {
      if (code === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keyup', eventListener);

    return () => document.removeEventListener('keyup', eventListener);
  }, [inputRef, handleClose]);

  useOnClickOutside(inputRef, handleClose);

  const isOpen = !!appList && !isLoading;

  return (
    <Popover isOpen={isOpen} closeOnBlur placement="bottom-start" gutter={8}>
      <PopoverTrigger>
        <Box
          display="flex"
          width={320}
          borderRadius="4"
          paddingLeft="8"
          paddingRight="12"
          paddingY="4"
          backgroundColor="rgba(255, 255, 255, .2)"
          alignItems="center"
          border="1px solid"
          borderColor="neutral.80"
          {...props}
          onTransitionEnd={() => inputRef.current?.focus()}
        >
          <Icon name="Magnifier" size="24" color="rgba(255, 255, 255, 0.8)" marginRight="8" />
          <Box
            as="input"
            autoFocus
            ref={inputRef}
            value={query}
            onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => setQuery(value)}
            width="100%"
            placeholder="Search projects in your workspaces..."
            fontSize="3"
            textColor="neutral.100"
            lineHeight="2"
            background="transparent"
            boxShadow="none"
            tabIndex={inputTabIndex}
            _placeholder={{
              color: 'neutral.80',
            }}
          />
          {query && <Icon name="Cross" size="24" onClick={() => setQuery('')} cursor="pointer" color="neutral.100" />}
        </Box>
      </PopoverTrigger>
      <PopoverContent paddingY={8}>
        <Box minWidth="320px">
          {query && appList && (
            <AppList
              apps={appList}
              isOpen={isOpen}
              onFocusReset={() => {
                inputRef.current?.focus();
              }}
            />
          )}
        </Box>
      </PopoverContent>
    </Popover>
  );
};

export default SearchBar;
